<template>
  <div
    :class="allClasses"
    tabindex="0"
    v-on="listeners"
    @keydown.enter.space.stop.prevent="delegateInput"
  >
    <div class="checkbox__icon">
      <EIcon class="checkbox__check" icon="check" size="s" />
    </div>
    <input
      ref="inputElement"
      v-bind="{ disabled, required, name, value, id, ...$attrs }"
      class="checkbox__input"
      type="checkbox"
      tabindex="-1"
      data-test-id="checkbox-input"
      :checked="checked"
      @click="onInput"
    >
  </div>
</template>

<script>
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';
import EIcon from '~~/common/vue/components/icon';

// TODO: implement ECheckboxes and update below
/**
 * A single checkbox input, whose value is `true` or `false`.
 *
 * For multiple checkboxes, use the `ECheckboxes` component (not implemented yet).
 */
export default {
  name: 'ECheckbox',
  components: {
    EIcon,
  },
  mixins: [classes],
  props: {
    id: {
      type: [String, null],
      default: null,
    },
    value: {
      type: [Boolean, String, Array],
      default: false,
    },
    name: {
      type: [String, Number],
      default: null,
    },
    size: {
      type: String,
      default: 'm',
      cssClass: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
  },
  computed: {
    listeners() {
      const { input, ...rest } = this.$listeners;
      return rest;
    },
    checked() {
      return !!this.value;
    },
    allClasses() {
      const allClasses = [...this.classes];
      if (this.checked) {
        allClasses.push(getModifierClass('checkbox', 'checked'));
      }
      return allClasses;
    },
  },
  methods: {
    onInput(event) {
      const value = event.target.checked;
      this.$emit('input', value);
    },
    delegateInput() {
      this.$refs.inputElement.click();
    },
  },
};
</script>

<style lang="scss" scoped>
$checkbox-size-m: $space-l;
$checkbox-size-s: 16px;
$tappable-area-padding: $space-1s;
$icon-padding: $space-2s / 2;

.checkbox {
  cursor: pointer;
  display: inline-block;
  position: relative;
  font-family: $family;
  line-height: $line-height-m;

  &::before {
    content: "";
    display: block;
    border-radius: $border-radius-s;
    border: solid $border-width var(--color-form-border-default);
    box-sizing: border-box;
    pointer-events: none;
    transition: border $timing-2s $easing;
    background: var(--color-button-content-primary); // Same color as the check icon.
  }

  &:hover {
    &::before {
      border-color: var(--color-form-border-hover);
    }
  }

  &:focus {
    @extend %keyboard-nav-focus;

    &::before {
      border-color: var(--color-form-border-primary-active);
    }
  }

  &__icon {
    position: absolute;
    top: 0;
    left: 0;
  }

  &__check {
    position: absolute;
    top: $icon-padding;
    left: $icon-padding;
    color: var(--color-button-content-primary);
  }

  &__input {
    border: none;
    cursor: pointer;
    height: calc(100% + #{2 * $tappable-area-padding});
    left: -1 * $tappable-area-padding;
    margin: 0;
    opacity: 0;
    position: absolute;
    top: -1 * $tappable-area-padding;
    width: calc(100% + #{2 * $tappable-area-padding});
  }

  &--size-s {
    font-size: $font-m;

    &::before {
      width: $checkbox-size-s;
      height: $checkbox-size-s;
    }

    &.checkbox--checked {
      &::before {
        border-width: $checkbox-size-s / 2;
        border-color: var(--color-brand-primary);
      }
    }
  }

  &--size-m {
    font-size: $font-l;

    &::before {
      width: $checkbox-size-m;
      height: $checkbox-size-m;
    }

    &.checkbox--checked {
      &::before {
        border-width: $checkbox-size-m / 2;
        border-color: var(--color-brand-primary);
      }
    }
  }

  &--disabled {
    pointer-events: none;
    color: var(--color-disabled);

    &::before {
      background-color: var(--color-background-secondary);
      border-color: var(--color-disabled);
    }

    .checkbox__icon {
      display: none;
    }

    &.checkbox--checked {
      &::before {
        border-color: var(--color-brand-primary);
      }

      .checkbox__icon {
        display: unset;
      }
    }
  }
}
</style>
